import type { DailyLog, HealthMetric, IsoDate } from "../domain/types.js";
import type { CheerRow } from "../../shared/types.js";
import type { DayMeasurement, MeasurementView } from "../domain/measurement-view.js";
import { dayMeasurement } from "../domain/measurement-view.js";
import { fmt } from "../domain/numbers.js";
import { Arrow } from "./Arrow.js";
import { dayCompletion } from "../domain/scoring.js";
import { workoutFor } from "../domain/workout.js";

export interface CalendarProps {
  /** 月份的所有日期格。null 代表補白格（月初補位或超出計畫期間）。 */
  cells: (IsoDate | null)[];
  today: IsoDate;
  selectedDate: IsoDate;
  logs: Record<IsoDate, Partial<DailyLog>>;
  healthMetrics: Record<IsoDate, Partial<HealthMetric>>;
  /**
   * 量測資料的視圖。格子裡要畫公斤還是箭頭由它決定——見 `measurement-view.ts`。
   */
  view: MeasurementView;
  /** 誰在哪一天戳過。整份傳進來，格數的計算在這裡做一次就好。 */
  cheers: CheerRow[];
  onSelect: (date: IsoDate) => void;
}

function OutsideCell() {
  return <div class="calendar-day outside" aria-hidden="true" />;
}

function DayCell({
  date, today, selectedDate, log, metric, day, cheerCount, onSelect
}: {
  date: IsoDate;
  today: IsoDate;
  selectedDate: IsoDate;
  log: Partial<DailyLog>;
  metric: Partial<HealthMetric>;
  /** 那天有沒有量、量了多少、是升是降。哪一個答得出來由 view 決定。 */
  day: DayMeasurement;
  cheerCount: number;
  onSelect: (date: IsoDate) => void;
}) {
  const [title, , cls] = workoutFor(date);
  const progress = dayCompletion(log, metric);
  const selected = date === selectedDate;

  // class 的順序與改寫前一致；styles.css 靠這些 class 決定每種課表的配色
  const classes = [
    "calendar-day",
    cls,
    date === today ? "today" : "",
    selected ? "selected" : "",
    progress.complete ? "complete" : ""
  ].filter(Boolean).join(" ");

  const weight = day.kind === "revealed"
      && day.measurement
      && Number.isFinite(day.measurement.weight_kg)
    ? `${fmt(day.measurement.weight_kg)}kg`
    : "";

  return (
    <button
      type="button" class={classes} data-date={date}
      aria-pressed={selected} onClick={() => onSelect(date)}
    >
      <span class="cal-date">{Number(date.slice(8, 10))}</span>
      {/* 角落的小記號，不動 Rank 色塊——那是格子的背景，兩者各答各的問題 */}
      {cheerCount > 0 && (
        <span class="cal-cheer" aria-label={`${cheerCount} 人戳過這天`}>♥{cheerCount}</span>
      )}
      <div class="cal-title">{title}</div>
      <div class="cal-meta">
        <span class="cal-count">{progress.done}/{progress.total}</span>
        {/*
          遮蔽時只換內容，class 與結構一格都不動——main.css 靠精確選取，
          改了會靜默失效（鐵則 7）。
        */}
        <span class="cal-weight">
          {day.kind === "revealed"
            ? weight
            : day.measured ? <Arrow direction={day.direction} /> : ""}
        </span>
      </div>
    </button>
  );
}

export function Calendar({
  cells, today, selectedDate, logs, healthMetrics, view, cheers, onSelect
}: CalendarProps) {
  // 一次算完，不要每格再掃一遍整份戳
  const counts: Record<IsoDate, number> = {};
  for (const cheer of cheers) counts[cheer.cheer_date] = (counts[cheer.cheer_date] || 0) + 1;

  return (
    <>
      {cells.map((date, index) => date === null
        ? <OutsideCell key={`outside-${index}`} />
        : (
          <DayCell
            key={date}
            date={date}
            today={today}
            selectedDate={selectedDate}
            log={logs[date] || {}}
            metric={healthMetrics[date] || {}}
            day={dayMeasurement(view, date)}
            cheerCount={counts[date] || 0}
            onSelect={onSelect}
          />
        ))}
    </>
  );
}
